/**
 * @file usePulseData.ts
 * @description Dedicated hook for near-real-time operational telemetry in the Pulse Tab.
 * 
 * CORE RESPONSIBILITIES:
 * 1. Queries live guest activity (last 15 minutes) and today's kitchen velocity.
 * 2. Listens to real-time postgres changes on `orders` and `waiter_requests`.
 * 3. Polls every 15 seconds to ensure fresh visitor footfall telemetry.
 */

import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../../lib/supabase';
import type { Venue } from '../../../types';
import type { PulseLiveActivity } from '../types';
import { fetchPulseLiveData } from '../services/analytics.service';

// In-memory cache keyed by venueId to guarantee synchronous rendering on page mount
const pulseDataCache = new Map<string, { data: PulseLiveActivity; timestamp: Date }>();

/**
 * Primes the in-memory pulse cache during the initial security/venue synchronization phase.
 * Guarantees that when the admin dashboard reveals, pulse telemetry is already present.
 */
export const primePulseDataCache = (venueId: string, data: PulseLiveActivity) => {
  pulseDataCache.set(venueId, { data, timestamp: new Date() });
};

export const getCachedPulseData = (venueId: string): PulseLiveActivity | null => {
  return pulseDataCache.get(venueId)?.data || null;
};

export const usePulseData = (activeVenue: Venue | null) => {
  const cached = activeVenue?.id ? pulseDataCache.get(activeVenue.id) : null;
  const [pulseData, setPulseData] = useState<PulseLiveActivity | null>(() => cached?.data || null);
  const [isLoading, setIsLoading] = useState(!cached);
  const [lastUpdated, setLastUpdated] = useState<Date>(() => cached?.timestamp || new Date());

  const loadData = useCallback(async () => {
    if (!activeVenue?.id) return;
    try {
      const data = await fetchPulseLiveData(activeVenue.id);
      setPulseData(data);
      const now = new Date();
      setLastUpdated(now);
      pulseDataCache.set(activeVenue.id, { data, timestamp: now });
    } catch (err) {
      console.warn('[usePulseData] Failed to fetch live pulse:', err);
    } finally {
      setIsLoading(false);
    }
  }, [activeVenue?.id]);

  useEffect(() => {
    if (!activeVenue?.id) {
      setPulseData(null);
      setIsLoading(false);
      return;
    }

    const currentCached = pulseDataCache.get(activeVenue.id);
    if (currentCached) {
      setPulseData(currentCached.data);
      setLastUpdated(currentCached.timestamp);
      setIsLoading(false);
    } else {
      setIsLoading(true);
    }

    loadData();

    // Pauses 15-second polling when the browser tab is hidden to save client & Supabase resources
    const interval = setInterval(() => {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      loadData();
    }, 15000);

    // Refreshes data immediately when the user switches back to this tab
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        loadData();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Debounces burst realtime events so multiple simultaneous changes trigger only one reload
    let debounceTimer: ReturnType<typeof setTimeout> | null = null;
    const triggerDebouncedReload = () => {
      if (debounceTimer) clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        loadData();
      }, 600);
    };

    // Subscribes to realtime updates on active orders and waiter calls
    const channel = supabase
      .channel(`venue_pulse_${activeVenue.id}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'orders', filter: `venue_id=eq.${activeVenue.id}` },
        triggerDebouncedReload
      )
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'waiter_requests', filter: `venue_id=eq.${activeVenue.id}` },
        triggerDebouncedReload
      )
      .subscribe();

    return () => {
      clearInterval(interval);
      if (debounceTimer) clearTimeout(debounceTimer);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      supabase.removeChannel(channel);
    };
  }, [activeVenue?.id, loadData]);

  return { pulseData, isLoading, lastUpdated, refresh: loadData };
};
